import { map, inRange, uniqBy } from 'lodash';
import { Hidden } from '@mui/material';
import React from 'react';

import { type Dispatch } from '@/app-state/store';

import { ALERT_STATUSES } from '@/constants/statuses';
import { H5 } from '@/components/type';
import { URLS } from '@/constants';
import { PrimaryButton } from '@/components/button';
import Alert from '@/components/alert';
import Icon, { iconNames } from '@/components/icons';
import MiniCartItem from '@/components/mini-cart-item';
import { Panel } from '@/components/panel';

import { CART_MAXIMUM, DISCOVERY_LINKS } from './constants';
import {
  AlertWrapper,
  StyledBody,
  StyledFooter,
  StyledH4,
  StyledHeader,
  StyledNavLink,
  StyledNavLinkText,
  StyledSmallTitle,
  StyledVehicleLinks,
} from './styles';
import { MinicartListings } from '@/components/similar-but-safer/components/mini-cart';
import { type VehicleSearchResult } from '@tac/vehicle-api-client/types';

// @ts-expect-error [🤖 PLEASE FIX 🤖]: Binding element 'links' implicitly has an 'any' type.
const LinksList = ({ links, onClose }) => {
  return map(links, (link, index) => (
    <StyledVehicleLinks key={`vehiclelink-${index}`}>
      <StyledNavLink tabIndex={0} onClick={onClose} href={link.href}>
        <H5 as="h2">
          <StyledNavLinkText>{link.linkText}</StyledNavLinkText>
          <StyledNavLinkText>
            <strong>{link.price}</strong>
          </StyledNavLinkText>
        </H5>
        <Icon name={iconNames.linkActive} />
      </StyledNavLink>
    </StyledVehicleLinks>
  ));
};

type MinicartProps = {
  alertMessage?: string;
  compareVehicleCount?: number;
  isOpen?: boolean;
  onClose: () => void;
  removeFromCompare: Dispatch['compare']['removeFromCompare'];
  vehicleIds?: string[];
  vehicles: VehicleSearchResult[];
};

const Minicart = ({
  isOpen = false,
  onClose,
  alertMessage = undefined,
  removeFromCompare,
  vehicleIds = [],
  vehicles = [],
  compareVehicleCount = 0,
}: MinicartProps) => {
  const canCompareVehicles = inRange(compareVehicleCount, 2, CART_MAXIMUM + 1);

  return (
    <Panel anchor="right" ariaLabelledBy="compare-panel" isOpen={isOpen} onClose={onClose}>
      <StyledBody>
        <StyledHeader>
          <H5 id="compare-panel" tabIndex={-1} as="h1">
            Compare: {compareVehicleCount}
          </H5>
        </StyledHeader>
        {map(uniqBy(vehicles, 'vehicleid'), (vehicle) => (
          <MiniCartItem key={vehicle?.vehicleid} {...{ vehicle, removeFromCompare }} />
        ))}
        {compareVehicleCount === 0 && (
          <>
            <StyledH4 as="h2">No cars selected</StyledH4>
            <StyledSmallTitle>Browse safe cars for:</StyledSmallTitle>
            <LinksList links={DISCOVERY_LINKS.NO_VEHICLE} onClose={onClose} />
          </>
        )}
        {compareVehicleCount === 1 && (
          <>
            <MinicartListings vehicle={vehicles[0]} />
            <LinksList links={DISCOVERY_LINKS.SINGLE_VEHICLE} onClose={onClose} />
          </>
        )}
        {compareVehicleCount > 1 && <LinksList links={DISCOVERY_LINKS.MULTIPLE_VEHICLES} onClose={onClose} />}
      </StyledBody>
      {compareVehicleCount > 0 && (
        <StyledFooter>
          {alertMessage && (
            <AlertWrapper>
              <Alert status={ALERT_STATUSES.error} content={alertMessage} />
            </AlertWrapper>
          )}
          <PrimaryButton
            disabled={!canCompareVehicles}
            href={`${URLS.COMPARE}?vehicles=${vehicleIds.join(',')}`}
            fullWidth
            disableRipple
            tracking={{
              event: 'compare_cars',
              action: 'compare',
              compared_vehicles:
                vehicles?.length > 0
                  ? vehicles.map((vehicle) => ({
                      make: vehicle.makedescription,
                      model: vehicle.modeldescription,
                    }))
                  : [],
            }}
          >
            Compare <Hidden mdDown>Cars</Hidden>
          </PrimaryButton>
        </StyledFooter>
      )}
    </Panel>
  );
};

export default Minicart;
